import { Modal, Table, Form, Spinner, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Bell, BellFill, BellSlashFill, EyeFill, EyeSlashFill, PeopleFill } from "react-bootstrap-icons";
import { useAuth } from "context/AuthContext";
import http from "services/httpClient";
import TriggerRecipientsModal from "components/device/TriggerRecipientsModal";
import HeartbeatRecipientsModal from "components/device/HeartbeatRecipientsModal";
import HeartbeatSettingsModal from "./HeartBeatSettingModal";

interface Label {
  id: number;
  label: string;
  column_index: number;
  is_enabled: boolean;
}

interface Recipient {
  id: number;
  device_id: number;
  column_index: number;
  user_id: number;
  alert_type: "email" | "sms" | "both" | null;
  enabled: boolean;
  alarm_cleared: boolean | null;
}

interface UserAlertModalProps {
  deviceId: number;
  clientId: number;
  show: boolean;
  onClose: () => void;
}

interface HeartbeatTrigger {
  id: number;
  device_id: number;
  user_id: number;
  subscribed: boolean;
  alarm: boolean;
  alert_type: "email" | "sms" | "both" | null;
}

export const UserAlertModal: React.FC<UserAlertModalProps> = ({
  deviceId,
  clientId,
  show,
  onClose,
}) => {
  const { user } = useAuth();
  const userId = user?.id;

  const [labels, setLabels] = useState<Label[]>([]);
  const [triggerMap, setTriggerMap] = useState<Map<number, any[]>>(new Map());
  const [recipientRowsMap, setRecipientRowsMap] = useState<Map<number, Recipient[]>>(new Map());
  const [recipientCountMap, setRecipientCountMap] = useState<Map<number, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [heartbeat, setHeartbeat] = useState<HeartbeatTrigger | null>(null);
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);

  const [recipientModalShow, setRecipientModalShow] = useState(false);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null);
  const [selectedTriggerLabel, setSelectedTriggerLabel] = useState<string>("");

  const [heartbeatRecipientModalShow, setHeartbeatRecipientModalShow] = useState(false);
  const [heartbeatRecipientCount, setHeartbeatRecipientCount] = useState(0);

  const [heartbeatRows, setHeartbeatRows] = useState<HeartbeatTrigger[]>([]);

  const [selectedDeviceId, setSelectedDeviceId] =  useState<number | null>(null);
  const [showHeartbeatModal, setShowHeartbeatModal] =  useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const hbRes = await http.post(`/heartbeat_trigger/device/${deviceId}/user/${userId}`);
      setHeartbeat(hbRes.data);

      const [hbListRes, rawRes, calcRes, triggerRes, featuresRes] = await Promise.all([
        http.get(`/heartbeat_trigger/device/${deviceId}`),
        http.get(`/device/${deviceId}/labels`),
        http.get(`/calculated_params/${deviceId}`),
        http.get(`/triggers/device/${deviceId}`),
        clientId ? http.get(`/client_features/${clientId}`) : Promise.resolve({ data: null }),
      ]);

      const hbList = hbListRes.data || [];
      setHeartbeatRows(hbList);
      setHeartbeatRecipientCount(hbList.length);
      setSmsEnabled(!!featuresRes.data?.sms_enabled);

      const rawLabels: Label[] = rawRes.data;
      const calcLabels: Label[] = (calcRes.data || []).map((c: any) => ({
        id: -c.column_index,
        label: c.result_label,
        column_index: c.column_index,
        is_enabled: true,
      }));

      const combinedLabels = [...rawLabels, ...calcLabels].filter((l) => l.is_enabled);
      combinedLabels.sort((a, b) => a.column_index - b.column_index);
      setLabels(combinedLabels);

      const triggers = triggerRes.data || [];
      const tMap = new Map<number, any[]>();

      for (const trig of triggers) {
        const list = tMap.get(trig.column_index) || [];
        list.push(trig);
        tMap.set(trig.column_index, list);
      }

      const rowsMap = new Map<number, Recipient[]>();
      const countMap = new Map<number, number>();

      await Promise.all(
        combinedLabels.map(async (label) => {
          const hasTrigger = (tMap.get(label.column_index) || []).length > 0;

          if (!hasTrigger) {
            countMap.set(label.column_index, 0);
            rowsMap.set(label.column_index, []);
            return;
          }

          const res = await http.get(
            `/triggers/recipients?device_id=${deviceId}&column_index=${label.column_index}`
          );

          const allRecipients: Recipient[] = res.data || [];
          countMap.set(label.column_index, allRecipients.length);
          rowsMap.set(label.column_index, allRecipients);
        })
      );

      setTriggerMap(tMap);
      setRecipientRowsMap(rowsMap);
      setRecipientCountMap(countMap);
    } catch (err) {
      console.error("UserAlertModal fetchData error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId, clientId]);

  const updateAlertType = async (id: number, alert_type: Recipient["alert_type"] | null) => {
    await http.patch(`/triggers/bulk_update/recipients/${id}`, { alert_type });
    await fetchData();
  };

  const toggleEnabled = async (id: number, enabled: boolean) => {
    await http.patch(`/triggers/bulk_update/recipients/${id}`, { enabled });
    await fetchData();
  };

  const clearAlarm = async (id: number) => {
    await http.post(`/triggers/bulk_update/recipients/${id}/clear`);
    await fetchData();
  };

  const openRecipientsModal = (columnIndex: number, label: string) => {
    setSelectedColumnIndex(columnIndex);
    setSelectedTriggerLabel(label);
    setRecipientModalShow(true);
  };

  const hasEmail = (alertType: Recipient["alert_type"]) =>
    alertType === "email" || alertType === "both";

  const hasSMS = (alertType: Recipient["alert_type"]) =>
    alertType === "sms" || alertType === "both";

  const hbHasEmail = (t: any) => t === "email" || t === "both";
const hbHasSMS = (t: any) => t === "sms" || t === "both";

const hbAllEmail =
  heartbeatRows.length > 0 &&
  heartbeatRows.every((r) => hbHasEmail(r.alert_type));

const hbAllSMS =
  heartbeatRows.length > 0 &&
  heartbeatRows.every((r) => hbHasSMS(r.alert_type));

const hbAllSubscribed =
  heartbeatRows.length > 0 &&
  heartbeatRows.every((r) => r.subscribed === true);

const hbAnyAlarm =
  heartbeatRows.length > 0 &&
  heartbeatRows.some((r) => r.alarm === true);

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Bell /> Alert Settings
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Email</th>
                  <th className={!smsEnabled ? "text-muted" : ""}>
                    <span>SMS</span>
                    {!smsEnabled && (
                      <OverlayTrigger placement="top" overlay={<Tooltip>SMS is disabled by owner/admin</Tooltip>}>
                        <span className="ms-1" style={{ cursor: "help" }}>
                          ⓘ
                        </span>
                      </OverlayTrigger>
                    )}
                  </th>
                  <th>Alarm</th>
                  <th>Subscribed</th>
                  <th>Recipients</th>
                </tr>
              </thead>

              <tbody>
                {heartbeat && (
                  <tr>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          setSelectedDeviceId(deviceId);
                          setShowHeartbeatModal(true);
                        }}
                      >
                        Device Offline
                      </Button>
                      {selectedDeviceId && (
                        <HeartbeatSettingsModal
                          open={showHeartbeatModal}
                          deviceId={selectedDeviceId}
                          onClose={() => {
                            setShowHeartbeatModal(false);
                            setSelectedDeviceId(null);
                          }}
                        />
                      )}
                    </td>

                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={hbAllEmail}
                        onChange={async (e) => {
                          const checked = e.target.checked;

                          await Promise.all(
                            heartbeatRows.map((r) => {
                              let newType;

                              if (checked) {
                                newType = hbAllSMS ? "both" : "email";
                              } else {
                                newType = hbAllSMS ? "sms" : null;
                              }

                              return http.patch(`/heartbeat_trigger/${r.id}`, { alert_type: newType });
                            })
                          );

                          await fetchData();
                        }}
                      />
                    </td>

                    <td className={!smsEnabled ? "text-muted" : ""}>
                      <Form.Check
                        type="checkbox"
                        disabled={!smsEnabled}
                        checked={hbAllSMS}
                        onChange={async (e) => {
                          if (!smsEnabled) return;

                          const checked = e.target.checked;

                          await Promise.all(
                            heartbeatRows.map((r) => {
                              let newType;

                              if (checked) {
                                newType = hbAllEmail ? "both" : "sms";
                              } else {
                                newType = hbAllEmail ? "email" : null;
                              }

                              return http.patch(`/heartbeat_trigger/${r.id}`, { alert_type: newType });
                            })
                          );

                          await fetchData();
                        }}
                      />
                    </td>

                    <td className="text-center">
                      {hbAnyAlarm ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Uncleared Alarm</Tooltip>}>
                          <BellFill
                            className="text-danger"
                            onClick={async () => {
                              await http.post(`/heartbeat_trigger/device/${deviceId}/clear_all`);
                              await fetchData();
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </OverlayTrigger>
                      ) : (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Cleared</Tooltip>}>
                          <BellSlashFill className="text-muted" />
                        </OverlayTrigger>
                      )}
                    </td>

                    <td className="text-center">
                      <span
                        onClick={async () => {
                          await Promise.all(
                            heartbeatRows.map((r) =>
                              http.patch(`/heartbeat_trigger/${r.id}`, {
                                subscribed: !hbAllSubscribed,
                              })
                            )
                          );

                          await fetchData();
                        }}
                      >
                        {hbAllSubscribed ? <EyeFill /> : <EyeSlashFill className="text-muted" />}
                      </span>
                    </td>

                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setHeartbeatRecipientModalShow(true);
                        }}
                      >
                        <PeopleFill className="me-1" />
                        {heartbeatRecipientCount}
                      </Button>
                    </td>
                  </tr>
                )}

                {labels.map((label) => {
                  const triggers = triggerMap.get(label.column_index) || [];
                  const hasTrigger = triggers.length > 0;

                  const recipients = recipientRowsMap.get(label.column_index) || [];

                  const isDisabled = !hasTrigger;
                  const recipientCount = recipientCountMap.get(label.column_index) || 0;
                  const hasRecipient = recipients.length > 0;

                  const isEmail = hasRecipient && recipients.every((r) => hasEmail(r.alert_type));
                  const isSMS = hasRecipient && recipients.every((r) => hasSMS(r.alert_type));
                  const isSubscribed = hasRecipient && recipients.every((r) => r.enabled === true);
                  const anyAlarm = hasRecipient && recipients.some((r) => r.alarm_cleared === true);

                  const anchorRecipient = recipients[0];

                  return (
                    <tr key={label.id} className={isDisabled ? "text-muted" : ""}>
                      <td>
                        {isDisabled ? (
                          <span className="text-muted">
                            {label.label} <small>[x{label.column_index}]</small>
                          </span>
                        ) : (
                          <>
                            {label.label} <small className="text-muted">[x{label.column_index}]</small>
                          </>
                        )}
                      </td>

                      <td>
                        <Form.Check
                          type="checkbox"
                          disabled={isDisabled || !hasRecipient}
                          checked={!!isEmail}
                          onChange={async (e) => {
                            if (isDisabled || !anchorRecipient?.id) return;

                            const checked = e.target.checked;

                            let newType: Recipient["alert_type"];
                            if (checked) {
                              newType = isSMS ? "both" : "email";
                            } else {
                              newType = isSMS ? "sms" : null;
                            }

                            await updateAlertType(anchorRecipient.id, newType);
                          }}
                        />
                      </td>

                      <td className={!smsEnabled ? "text-muted" : ""}>
                        <Form.Check
                          type="checkbox"
                          disabled={isDisabled || !smsEnabled || !hasRecipient}
                          checked={!!isSMS}
                          onChange={async (e) => {
                            if (isDisabled || !smsEnabled || !anchorRecipient?.id) return;

                            const checked = e.target.checked;

                            let newType: Recipient["alert_type"];
                            if (checked) {
                              newType = isEmail ? "both" : "sms";
                            } else {
                              newType = isEmail ? "email" : null;
                            }

                            await updateAlertType(anchorRecipient.id, newType);
                          }}
                        />
                      </td>

                      <td className="text-center">
                        {!isDisabled && hasRecipient && anchorRecipient ? (
                          anyAlarm ? (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Uncleared Alarm</Tooltip>}>
                              <BellFill
                                className="text-danger"
                                onClick={() => clearAlarm(anchorRecipient.id)}
                                style={{ cursor: "pointer" }}
                              />
                            </OverlayTrigger>
                          ) : (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Cleared</Tooltip>}>
                              <BellSlashFill className="text-muted" />
                            </OverlayTrigger>
                          )
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>

                      <td className="text-center">
                        {!isDisabled && hasRecipient && anchorRecipient ? (
                          <span
                            onClick={() => toggleEnabled(anchorRecipient.id, !isSubscribed)}
                            style={{ cursor: "pointer" }}
                          >
                            {isSubscribed ? <EyeFill /> : <EyeSlashFill className="text-muted" />}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>

                      <td className="text-center">
                        {!isDisabled ? (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              openRecipientsModal(
                                label.column_index,
                                `${label.label} [x${label.column_index}]`
                              )
                            }
                          >
                            <PeopleFill className="me-1" />
                            {recipientCount}
                          </Button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={onClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <HeartbeatRecipientsModal
        show={heartbeatRecipientModalShow}
        onClose={() => setHeartbeatRecipientModalShow(false)}
        deviceId={deviceId}
        clientId={clientId}
        smsEnabled={smsEnabled}
        onRecipientsChanged={fetchData}
      />

      <TriggerRecipientsModal
        show={recipientModalShow}
        onClose={() => setRecipientModalShow(false)}
        deviceId={deviceId}
        columnIndex={selectedColumnIndex}
        triggerLabel={selectedTriggerLabel}
        clientId={clientId}
        smsEnabled={smsEnabled}
        onRecipientsChanged={fetchData}
      />
    </>
  );
};

export default UserAlertModal;